import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import {
    MaskPhone,
    MaskEmail,
    FormatMexicoDate,
    DownloadPassCard
} from '../../../../utilities/BL/Xtp';
import { useFloatMessage, MESSAGE_TYPE } from './../../../Providers/FloatMessage';

export const XtpGeneratePassModal = ({
    isOpen,
    participant,
    participants = [],
    currentIndex = -1,
    onNavigate,
    onClose
}) => {
    const { showMessage } = useFloatMessage();
    const [isDownloading, setIsDownloading] = useState(false);
    const passCardRef = useRef(null);

    const currentParticipant = (Array.isArray(participants) && currentIndex >= 0 && currentIndex < participants.length)
        ? participants[currentIndex]
        : participant;

    const hasMultiple = Array.isArray(participants) && participants.length > 1;
    const hasPrev = hasMultiple && currentIndex > 0;
    const hasNext = hasMultiple && currentIndex < participants.length - 1;

    const handlePrev = () => {
        if (hasPrev && onNavigate) {
            onNavigate(currentIndex - 1);
        }
    };

    const handleNext = () => {
        if (hasNext && onNavigate) {
            onNavigate(currentIndex + 1);
        }
    };

    // Navegación con flechas del teclado (← / →) y Escape para cerrar
    useEffect(() => {
        if (!isOpen) return;

        const handleKeyDown = (e) => {
            if (e.key === 'ArrowLeft' && hasPrev) {
                e.preventDefault();
                handlePrev();
            } else if (e.key === 'ArrowRight' && hasNext) {
                e.preventDefault();
                handleNext();
            } else if (e.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, hasPrev, hasNext, currentIndex, onNavigate, onClose]);

    if (!isOpen || !currentParticipant) return null;

    const handleDownload = async () => {
        if (isDownloading || !currentParticipant) return;
        setIsDownloading(true);

        try {
            const qrSvgElement = passCardRef.current?.querySelector('svg');
            const options = {
                qrElementSelector: '#admin-modal-xtp-pass-card svg',
                qrElement: qrSvgElement,
                bannerTitle: 'PASE PERSONAL EXCLUSIVO',
                hideHeader: true,
                hidePrize: true,
                hideLocationNote: true,
                sponsorLogoUrl: '/img/page/xolos-five-black.png',
                backgroundImageUrl: '/img/page/backgrounds/Fondo-SelfieFan-Varonil.jpeg'
            };

            const success = await DownloadPassCard(currentParticipant, options);

            if (success) {
                showMessage({
                    message: `¡Pase oficial de ${currentParticipant.name} guardado con éxito!`,
                    type: MESSAGE_TYPE.SUCCESS
                });
            } else {
                showMessage({
                    message: 'No fue posible guardar la tarjeta del pase.',
                    type: MESSAGE_TYPE.ERROR
                });
            }
        } catch (err) {
            console.error('Error al descargar tarjeta desde modal admin:', err);
            showMessage({
                message: 'Error inesperado al guardar la tarjeta del pase.',
                type: MESSAGE_TYPE.ERROR
            });
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div
            className="modal show d-block"
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.75)', zIndex: 1070 }}
            tabIndex="-1"
            onClick={onClose}
        >
            <div
                className="modal-dialog modal-dialog-centered"
                style={{ maxWidth: '480px' }}
                onClick={(e) => e.stopPropagation()}
            >
                <div className="modal-content border-0 shadow-2xl rounded-4 overflow-hidden bg-dark text-white">
                    {/* Header del Modal */}
                    <div className="modal-header border-bottom border-secondary border-opacity-25 px-4 py-3 bg-dark">
                        <div className="d-flex align-items-center gap-2">
                            <i className="bi bi-card-image text-warning fs-5"></i>
                            <div>
                                <h5 className="modal-title fw-bold text-white mb-0 fs-6">
                                    Generar Pase Oficial
                                </h5>
                                <small className="text-secondary" style={{ fontSize: '0.78rem' }}>
                                    {currentParticipant.name} • {currentParticipant.passCode}
                                </small>
                            </div>
                        </div>
                        <button
                            type="button"
                            className="btn-close btn-close-white"
                            onClick={onClose}
                            aria-label="Cerrar"
                        ></button>
                    </div>

                    {/* Cuerpo del Modal */}
                    <div className="modal-body p-3 p-sm-4 bg-dark bg-opacity-75" style={{ maxHeight: '82vh', overflowY: 'auto' }}>
                        {/* 1. Barra de Navegación Anterior / Siguiente si hay múltiples registros */}
                        {hasMultiple && (
                            <div className="d-flex align-items-center justify-content-between bg-black bg-opacity-50 p-2 rounded-3 mb-3 border border-secondary border-opacity-25">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning d-flex align-items-center gap-1 fw-bold"
                                    onClick={handlePrev}
                                    disabled={!hasPrev || isDownloading}
                                    title="Pase anterior (Flecha izquierda)"
                                >
                                    <i className="bi bi-chevron-left"></i>
                                    <span>Anterior</span>
                                </button>
                                <div className="text-center px-2">
                                    <span className="badge bg-warning text-dark fw-bold px-2 py-1 mb-1">
                                        Pase {currentIndex + 1} de {participants.length}
                                    </span>
                                    <div className="text-truncate text-white small fw-semibold" style={{ maxWidth: '180px' }}>
                                        {currentParticipant.name || 'Aficionado'}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning d-flex align-items-center gap-1 fw-bold"
                                    onClick={handleNext}
                                    disabled={!hasNext || isDownloading}
                                    title="Siguiente pase (Flecha derecha)"
                                >
                                    <span>Siguiente</span>
                                    <i className="bi bi-chevron-right"></i>
                                </button>
                            </div>
                        )}

                        {/* 2. Botón Superior con Leyenda informativa solicitada */}
                        <div className="text-center mb-3">
                            <p className="small mb-2 text-white" style={{ fontSize: '0.85rem' }}>
                                Guárdalo o toma captura de pantalla
                            </p>
                            <button
                                type="button"
                                className="btn btn-warning w-100 py-3 rounded-3 fw-black text-uppercase shadow-lg fs-6 d-flex align-items-center justify-content-center gap-2"
                                style={{
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                    color: '#ffffff',
                                    border: 'none',
                                    letterSpacing: '0.04em'
                                }}
                                onClick={handleDownload}
                                disabled={isDownloading}
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        <span>Guardando...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-camera-fill fs-5"></i>
                                        <span>Guardar en mi Galería</span>
                                    </>
                                )}
                            </button>
                        </div>

                        {/* 3. Tarjeta Dorada Central (Contenida limpiamente dentro del modal, idéntica a /passtpx) */}
                        <div className="d-flex justify-content-center my-2">
                            <div
                                className="XtpFan__PassCard m-0 shadow-lg"
                                id="admin-modal-xtp-pass-card"
                                ref={passCardRef}
                                style={{ maxWidth: '420px', width: '100%' }}
                            >
                                <div className="XtpFan__ShimmerBanner">
                                    <div className="XtpFan__ShimmerBannerOverlay"></div>
                                    <h2 className="font-cinzel fs-4 font-black text-white text-uppercase tracking-widest mb-0 drop-shadow">
                                        Pase Personal Exclusivo
                                    </h2>
                                    <p className="text-white small fw-bold mt-1 mb-2 tracking-wide">
                                        Tradiciones Perronas
                                    </p>
                                    <div className="XtpFan__FolioBadge">
                                        {currentParticipant.passCode || '#TPX26-00000'}
                                    </div>
                                </div>

                                {/* QR con Logotipo Personalizado Big.png y Nivel Q */}
                                <div className="d-flex justify-content-center my-3 p-2 bg-light rounded-3 border">
                                    <QRCodeSVG
                                        value={currentParticipant.verificationToken || currentParticipant.passCode || 'XTP-2026'}
                                        size={175}
                                        bgColor="#ffffff"
                                        fgColor="#172554"
                                        level="Q"
                                        includeMargin={true}
                                        imageSettings={{
                                            src: '/img/favicon/Big.png',
                                            x: undefined,
                                            y: undefined,
                                            height: 42,
                                            width: 42,
                                            excavate: true
                                        }}
                                    />
                                </div>

                                {/* Datos Protegidos del Aficionado */}
                                <div className="small text-muted mb-2 text-center">
                                    <span className="fw-bold text-dark fs-6">
                                        {(currentParticipant.name || 'AFICIONADO').toUpperCase()}
                                    </span>
                                    <br />
                                    <span>
                                        {MaskEmail(currentParticipant.email)} • {MaskPhone(currentParticipant.phone)}
                                    </span>
                                    <br />
                                    <span className="font-monospace text-dark fw-bold">
                                        Ticket: {currentParticipant.ticketNumber}
                                    </span>
                                    <br />
                                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>
                                        Registrado el: {FormatMexicoDate(currentParticipant.createdAtUtc)}
                                    </span>
                                </div>

                                {/* Patrocinador y Logotipo Oficial */}
                                <div className="mt-3">
                                    <p className="text-muted text-uppercase mb-2 fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.15em' }}>
                                        Powered by
                                    </p>
                                    <div className="XtpFan__SponsorLogos">
                                        <img
                                            src="/img/page/xolos-five-black.png"
                                            alt="Club Tijuana"
                                            onError={(e) => { e.target.style.display = 'none'; }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 4. Botón Inferior con Leyenda informativa solicitada */}
                        <div className="text-center mt-3">
                            <button
                                type="button"
                                className="btn btn-warning w-100 py-3 rounded-3 fw-black text-uppercase shadow-lg fs-6 d-flex align-items-center justify-content-center gap-2"
                                style={{
                                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                    color: '#ffffff',
                                    border: 'none',
                                    letterSpacing: '0.04em'
                                }}
                                onClick={handleDownload}
                                disabled={isDownloading}
                            >
                                {isDownloading ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        <span>Generando imagen de alta resolución...</span>
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-camera-fill fs-5"></i>
                                        <span>Guardar en mi Galería</span>
                                    </>
                                )}
                            </button>
                            <p className="small mt-2 mb-0 text-white" style={{ fontSize: '0.85rem' }}>
                                Guárdalo o toma captura de pantalla
                            </p>
                        </div>
                    </div>

                    {/* Footer del Modal */}
                    <div className="modal-footer border-top border-secondary border-opacity-25 px-4 py-2 bg-dark d-flex justify-content-between align-items-center">
                        {hasMultiple ? (
                            <div className="d-flex gap-2">
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning"
                                    onClick={handlePrev}
                                    disabled={!hasPrev || isDownloading}
                                >
                                    <i className="bi bi-arrow-left me-1"></i> Anterior
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-warning"
                                    onClick={handleNext}
                                    disabled={!hasNext || isDownloading}
                                >
                                    Siguiente <i className="bi bi-arrow-right ms-1"></i>
                                </button>
                            </div>
                        ) : (
                            <div></div>
                        )}
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-light px-4 rounded-3"
                            onClick={onClose}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};
